import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Bot, Search, Building, Sparkles } from 'lucide-react';
import './Leads.css';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

export default function Leads() {
  const { leads, fetchLeads, changeLeadStatus, scanLeads, loading, addClient, projectTemplates, addProposal, setCurrentSection } = useApp();
  const [selectedLead, setSelectedLead] = useState(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [scanLocation, setScanLocation] = useState('');
  const [scanIndustry, setScanIndustry] = useState('');
  const [scanProvider, setScanProvider] = useState('google');
  const [useLiveScan, setUseLiveScan] = useState(false);
  const [scanLimit, setScanLimit] = useState(6);
  const [scanMessage, setScanMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter(l => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (search && !l.companyName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const startScan = async () => {
    if (!scanLocation || !scanIndustry) return alert('Enter location and industry');
    setIsScanning(true);
    try {
      const response = await scanLeads(scanLocation, scanIndustry, {
        provider: scanProvider,
        useLive: useLiveScan,
        maxResults: Number(scanLimit) || 6,
      });
      setScanMessage(response?.note || response?.message || '');
    } catch (e) {
      console.error('Scan failed', e);
      const msg = !e?.response ? 'Network or CORS error. Verify server is running and CORS allows this origin.' : (e.response?.data?.error || e.response?.data?.message || e.message);
      alert(msg);
      setScanMessage('');
    } finally { setIsScanning(false); }
  };

  // helper: copy to clipboard with fallback
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard');
    } catch (e) {
      // fallback
      const ok = window.prompt('Copy the text below', text);
      if (ok === null) {
        // cancelled
      }
    }
  };

  const syntaxHighlight = (obj) => {
    let json = typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2);
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return json.replace(/("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key';
        } else {
          cls = 'json-string';
        }
      } else if (/true|false/.test(match)) cls = 'json-boolean';
      else if (/null/.test(match)) cls = 'json-null';
      return '<span class="' + cls + '">' + match + '</span>';
    });
  };

  const handleCreateClient = async () => {
    if (!selectedLead) return;
    try {
      const payload = {
        name: selectedLead.companyName,
        email: selectedLead.contactEmail,
        website: selectedLead.website,
        industry: selectedLead.industry,
        location: selectedLead.location,
        notes: selectedLead.reason,
      };
      const c = await addClient(payload);
      await changeLeadStatus(selectedLead._id, {
        status: 'converted',
        convertedClientId: c._id,
        note: 'Lead converted to client from AI Lead Gen',
      });
      alert(`Client created: ${c.name}`);
      setSelectedLead(null);
    } catch (e) {
      console.error(e);
      alert('Failed to create client');
    }
  };

  const formatCurrency = (n) => `$${(Math.round(n*100)/100).toLocaleString()}`;

  const suggestItemsForLead = (lead) => {
    const suggestions = [];
    // heuristics from reason and fields
    const reason = (lead.reason || '').toLowerCase();
    if (!lead.website) {
      suggestions.push('New responsive website with modern design');
      suggestions.push('Basic SEO and local listings');
    } else if (reason.includes('outdat') || reason.includes('redesign')) {
      suggestions.push('Website redesign with updated UX');
      suggestions.push('Accessibility & mobile optimization');
    }
    if (reason.includes('slow') || reason.includes('performance')) {
      suggestions.push('Performance optimization (speed & caching)');
    }
    if (reason.includes('non-responsive') || reason.includes('mobile')) {
      suggestions.push('Responsive redesign for mobile devices');
    }
    if (reason.includes('no seo') || reason.includes('search') || reason.includes('traffic')) {
      suggestions.push('SEO audit & local search optimization');
    }
    // dedupe
    return Array.from(new Set(suggestions));
  };

  const generateProposalEmail = (lead) => {
    const issues = suggestItemsForLead(lead);
    // choose a short summary for subject
    let summary = issues[0] || (lead.reason || 'website improvements');
    if (typeof summary === 'string' && summary.length > 40) summary = summary.slice(0,40) + '...';

    // ballpark estimate using projectTemplates (try website template) - sum top 2 items rates or fallback
    let ballpark = '';
    try {
      const tmpl = projectTemplates?.website || Object.values(projectTemplates || {})[0];
      if (tmpl) {
        // flatten categories and pick few rates
        const allItems = Object.values(tmpl.categories || {}).flat().map(i => i.rate || 0).sort((a,b)=>b-a);
        const sample = allItems.slice(0,3);
        const estimate = sample.length ? (sample.reduce((s,v)=>s+v,0) * 0.6) : 0; // conservative bundle
        if (estimate > 0) ballpark = `Starting around ${formatCurrency(estimate)} for an initial phase`;
      }
    } catch(e){ /* ignore */ }

    const subject = `Proposal: ${summary} — ${lead.companyName}`;

    const bodyLines = [];
    bodyLines.push(`Hi ${lead.companyName},`);
    bodyLines.push('');
    bodyLines.push(`I took a look at your ${lead.website ? 'website' : 'online presence' } and noticed the following:`);
    bodyLines.push('');
    if (lead.reason) bodyLines.push(`• ${lead.reason}`);
    issues.forEach(i => bodyLines.push(`• ${i}`));
    bodyLines.push('');
    bodyLines.push('Based on that, here is a short plan I recommend:');
    bodyLines.push('');
    // short plan bullets mapped from issues
    if (issues.length === 0) {
      bodyLines.push('• Quick audit to identify top conversion and speed improvements');
      bodyLines.push('• Simple redesign where needed and a phased rollout');
    } else {
      issues.slice(0,5).forEach((it,idx) => bodyLines.push(`• ${it} — implementation in a short phase ${idx+1}`));
    }
    bodyLines.push('');
    if (ballpark) bodyLines.push(`${ballpark}. This is a rough starting estimate — I’ll give a firm scope & price after a 15-min call.`);
    bodyLines.push('');
    bodyLines.push('Would you be available for a quick 15-minute call next week to review these ideas and a simple plan?');
    bodyLines.push('');
    bodyLines.push('Best,');
    bodyLines.push('PageHyve Team');

    const body = bodyLines.join('\n');
    return { subject, body };
  };

  const markContacted = async () => {
    if (!selectedLead) return;
    try {
      const updated = await changeLeadStatus(selectedLead._id, 'contacted');
      setSelectedLead(updated);
      alert('Lead marked as contacted');
    } catch (e) {
      console.error(e);
      alert('Failed to update status');
    }
  };

  const startProposal = async () => {
    if (!selectedLead) return;
    try {
      // Create a lead-linked draft proposal. Lead status moves to proposal_sent on actual send.
      await scaffoldProposal();
    } catch (e) {
      console.error('startProposal error', e);
      const msg = e?.response?.data?.error || e?.message || 'Failed to start proposal';
      alert(msg);
    }
  };

  const openProposalComposer = () => {
    if (!selectedLead) return;
    const { subject, body } = generateProposalEmail(selectedLead);
    setEmailSubject(subject);
    setEmailBody(body);
    setEmailComposerOpen(true);
  };

  const sendEmail = async (to) => {
    const toAddr = to || selectedLead?.contactEmail || '';
    const href = `mailto:${encodeURIComponent(toAddr)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    // attempt to mark as contacted, best-effort
    try {
      if (selectedLead?._id) {
        const updated = await changeLeadStatus(selectedLead._id, 'contacted');
        setSelectedLead(updated);
      }
    } catch (e) {
      console.error('Failed to mark contacted', e);
    }
    window.location.href = href;
    setEmailComposerOpen(false);
  };

  const scaffoldProposal = async () => {
    if (!selectedLead) return;
    try {
      // pick a reasonable template (try website by industry, fallback to first template)
      let items = [];
      try {
        if (projectTemplates && Object.keys(projectTemplates).length > 0) {
          const prefer = Object.keys(projectTemplates).find(k => k.toLowerCase().includes('website')) || Object.keys(projectTemplates)[0];
          const tmpl = projectTemplates[prefer];
          const catKeys = Object.keys(tmpl.categories || {});
          for (let i = 0; i < catKeys.length && items.length < 3; i++) {
            const arr = tmpl.categories[catKeys[i]] || [];
            for (let j = 0; j < arr.length && items.length < 3; j++) {
              items.push({ name: arr[j].name, rate: arr[j].rate || 0, quantity: 1 });
            }
          }
        }
      } catch (e) {
        console.warn('Template-based item selection failed', e);
      }

      // fallback: derive items from suggested issues
      if (!items || items.length === 0) {
        const issues = suggestItemsForLead(selectedLead);
        if (issues.length === 0) issues.push('Audit & quick improvements');
        const defaultRates = [1200, 600, 300];
        items = issues.slice(0,3).map((it, i) => ({ name: it, rate: defaultRates[i] || 200, quantity: 1 }));
      }

      const proposal = await addProposal({
        leadId: selectedLead._id,
        clientName: selectedLead.companyName || '',
        clientEmail: selectedLead.contactEmail || '',
        clientCompany: selectedLead.companyName || '',
        items,
        notes: selectedLead.reason
      });
      alert(`Draft proposal created (${proposal.proposalNumber || proposal._id}). Opening proposals.`);
      setSelectedLead(null);
      setCurrentSection('proposals');
    } catch (e) {
      console.error('scaffoldProposal error', e);
      alert(e.response?.data?.error || e.message || 'Failed to scaffold proposal');
    }
  };

  return (
    <div className="leads-page">
      <div className="leads-title-row">
        <Bot style={{ width: '2rem', height: '2rem', color: '#34d399' }} />
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>AI Lead Generation</h1>
      </div>

      <div className="leads-scanner-card" style={{ ...card, padding: '1.5rem', marginBottom: '2rem', background: 'linear-gradient(to right, rgba(16,185,129,0.1), rgba(6,182,212,0.1))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}><Sparkles style={{ width: '1.25rem', height: '1.25rem', color: '#34d399' }} /><h2 style={{ fontWeight: 600 }}>AI Scanner</h2></div>
        <div className="leads-scan-grid">
          <input value={scanLocation} onChange={e => setScanLocation(e.target.value)} placeholder="Location (e.g., Austin, TX)" style={input} />
          <select value={scanIndustry} onChange={e => setScanIndustry(e.target.value)} style={{ ...input, color: scanIndustry ? '#fff' : '#64748b' }}>
            <option value="">Select industry...</option>
            {['Restaurant', 'Retail', 'Services', 'Healthcare', 'Real Estate', 'Automotive'].map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select value={scanProvider} onChange={e => setScanProvider(e.target.value)} style={input}>
            <option value="google">Google</option>
            <option value="bing">Microsoft/Bing</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', color: '#e2e8f0' }}>
            <input id="use-live-scan" type="checkbox" checked={useLiveScan} onChange={(e) => setUseLiveScan(e.target.checked)} style={{ width: '0.95rem', height: '0.95rem' }} />
            <label htmlFor="use-live-scan" style={{ cursor: 'pointer', fontSize: '0.95rem', lineHeight: 1.25, fontWeight: 500, color: '#cbd5e1' }}>Use live provider</label>
          </div>
          <input type="number" min="2" max="20" value={scanLimit} onChange={(e) => setScanLimit(e.target.value)} placeholder="Max results" style={input} />
          <button onClick={startScan} disabled={isScanning || loading} style={{ ...btn, opacity: isScanning ? 0.5 : 1 }}><Search style={{ width: '1rem', height: '1rem' }} />{isScanning ? 'Scanning...' : 'Start Scan'}</button>
        </div>
        {scanMessage ? (
          <div style={{ marginTop: '0.75rem', fontSize: '0.86rem', color: '#bfdbfe' }}>{scanMessage}</div>
        ) : null}
      </div>

      <div className="leads-toolbar">
        <div style={{ flex: 1, position: 'relative' }}>
          <Search style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: '#64748b' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." style={{ ...input, paddingLeft: '3rem' }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={input} className="leads-filter-select">
          <option value="all">All Status</option>
          {['new', 'contacted', 'qualified', 'proposal_sent', 'revision_requested', 'converted', 'lost'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      <div style={{ ...card }} className="responsive-table leads-table-wrap">
        <table>
          <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {['Company', 'Issue', 'Score', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '1rem 1.5rem', fontSize: '0.875rem', fontWeight: 500, color: '#94a3b8' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map(lead => (
              <tr key={lead._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td data-label="Company" style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Building style={{ width: '1.5rem', height: '1.5rem', color: '#94a3b8' }} />
                    <div>
                      <div onClick={() => setSelectedLead(lead)} style={{ cursor: 'pointer' }}>
                        <p style={{ fontWeight: 500, margin: 0 }}>{lead.companyName}</p>
                        <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>{lead.industry} • {lead.location}</p>
                      </div>
                    </div>
                  </div>
                </td>
                <td data-label="Issue" style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>{lead.reason}</td>
                <td data-label="Score" style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>{lead.score}</td>
                <td data-label="Status" style={{ padding: '1rem 1.5rem' }}>
                  <select value={lead.status} onChange={e => changeLeadStatus(lead._id, e.target.value)} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', borderRadius: '0.5rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}>
                    {['new', 'contacted', 'qualified', 'proposal_sent', 'revision_requested', 'converted', 'lost'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No leads found</div>}
      </div>

      {selectedLead && (
        <div className="leads-modal-overlay" onClick={() => setSelectedLead(null)}>
          <div onClick={e => e.stopPropagation()} className="leads-modal">
            <div className="leads-modal-head">
              <div>
                <h3 style={{ margin: 0 }}>{selectedLead.companyName}</h3>
                <div style={{ color: '#64748b' }}>{selectedLead.industry} • {selectedLead.location}</div>
              </div>
              <div className="leads-modal-actions">
                {selectedLead.contactEmail && <button onClick={() => copyToClipboard(selectedLead.contactEmail)} style={{ background: '#f3f4f6', border: '1px solid #e6eef8', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Copy Email</button>}
                {selectedLead.contactEmail && <button onClick={() => window.location.href = `mailto:${selectedLead.contactEmail}?subject=We can help with ${encodeURIComponent(selectedLead.companyName)}`} style={{ background: '#2563eb', color: '#fff', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Send Email</button>}
                <button onClick={markContacted} style={{ background: '#f59e0b', color: '#071127', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Mark Contacted</button>
                <button onClick={startProposal} style={{ background: '#7c3aed', color: '#fff', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Start Proposal</button>
                <button onClick={openProposalComposer} style={{ background: '#06b6d4', color: '#071127', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Propose Email</button>
                <button onClick={handleCreateClient} style={{ background: '#10b981', color: '#fff', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Create Client</button>
                <button onClick={() => copyToClipboard(JSON.stringify(selectedLead, null, 2))} style={{ background: 'transparent', border: '1px solid #e6eef8', padding: '0.4rem 0.6rem', borderRadius: 6 }}>Copy JSON</button>
                <button onClick={() => setSelectedLead(null)} style={{ background: 'transparent', border: 'none', fontSize: '1rem' }}>Close</button>
              </div>
            </div>

            <div className="leads-modal-grid">
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Overview</h4>
                <p style={{ margin: 0 }}>{selectedLead.reason}</p>
                <div style={{ marginTop: '0.75rem', color: '#374151' }}>
                  <div><strong>Score:</strong> {selectedLead.score}</div>
                  <div><strong>Status:</strong> {selectedLead.status}</div>
                </div>
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Contact</h4>
                <div style={{ color: '#374151' }}>
                  {selectedLead.website && <div><strong>Website:</strong> <a href={selectedLead.website} target="_blank" rel="noreferrer">{selectedLead.website}</a></div>}
                  {selectedLead.contactEmail && <div><strong>Email:</strong> {selectedLead.contactEmail}</div>}
                  {(selectedLead.contactPhone || selectedLead.phone) && <div><strong>Phone:</strong> {selectedLead.contactPhone || selectedLead.phone}</div>}
                  {selectedLead.employees && <div><strong>Employees:</strong> {selectedLead.employees}</div>}
                  {selectedLead.revenue && <div><strong>Revenue:</strong> {selectedLead.revenue}</div>}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Lead Timeline</h4>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.6rem 0.75rem', marginBottom: '0.8rem', maxHeight: 180, overflowY: 'auto' }}>
                {Array.isArray(selectedLead.activities) && selectedLead.activities.length ? (
                  selectedLead.activities
                    .slice()
                    .sort((a, b) => new Date(b.at || b.createdAt || 0) - new Date(a.at || a.createdAt || 0))
                    .slice(0, 8)
                    .map((entry, idx) => (
                      <div key={`${entry.at || idx}-${entry.type || idx}`} style={{ padding: '0.35rem 0', borderBottom: idx < Math.min(selectedLead.activities.length, 8) - 1 ? '1px solid #e2e8f0' : 'none' }}>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{new Date(entry.at || entry.createdAt || Date.now()).toLocaleString()}</div>
                        <div style={{ fontSize: '0.88rem', color: '#0f172a', fontWeight: 600 }}>{String(entry.type || 'activity').replaceAll('_', ' ')}</div>
                        {entry.note ? <div style={{ fontSize: '0.84rem', color: '#334155' }}>{entry.note}</div> : null}
                      </div>
                    ))
                ) : (
                  <div style={{ fontSize: '0.84rem', color: '#64748b' }}>No timeline events yet.</div>
                )}
              </div>

              <h4 style={{ margin: '0 0 0.5rem 0' }}>Raw Data</h4>
              <div style={{ background: '#f3f4f6', padding: '0.75rem', borderRadius: 6, overflowX: 'auto' }}>
                <style>{`.json-key{color:#b45309;font-weight:600}.json-string{color:#065f46}.json-number{color:#175ddc}.json-boolean{color:#b91c1c}.json-null{color:#6b7280};pre.json{margin:0;font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Source Code Pro', monospace;font-size:0.9rem}`}</style>
                <pre className="json" style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: syntaxHighlight(selectedLead) }} />
              </div>

              {emailComposerOpen && (
                <div style={{ marginTop: '0.75rem', background: '#fff', border: '1px solid #e6eef8', padding: '0.75rem', borderRadius: 6 }}>
                  <div className="leads-email-top">
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>To</label>
                      <input value={selectedLead.contactEmail || ''} readOnly style={{ width: '100%', padding: '0.5rem', borderRadius: 6, background: '#f8fafc' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Subject</label>
                      <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: 6 }} />
                    </div>
                  </div>

                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Message</label>
                    <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} style={{ width: '100%', minHeight: 120, padding: '0.5rem', borderRadius: 6 }} />
                  </div>

                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => setEmailComposerOpen(false)} style={{ padding: '0.5rem 0.75rem', background: 'transparent', border: '1px solid #e6eef8', borderRadius: 6 }}>Cancel</button>
                    <button onClick={() => sendEmail()} style={{ padding: '0.5rem 0.75rem', background: '#2563eb', color: '#fff', borderRadius: 6 }}>Send Email</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
